import React, { useState, Component } from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, HelpCircleIcon, GitCompareIcon, UserCheckIcon, StarIcon, XIcon, CheckCircle2Icon, ArrowRightIcon, FileTextIcon, GlobeIcon, BookOpenIcon, ClockIcon, SparklesIcon, LanguagesIcon, RepeatIcon, ScrollTextIcon, SlidersHorizontalIcon } from 'lucide-react';
import { AtnaAnalytics } from './AtnaAnalytics';
type SubTab = 'gerer' | 'analytiques' | 'configurer';
type ConfigSection = 'connaissances' | 'automatisations' | 'gouvernance';
interface KpiCard {
  label: string;
  value: string;
  tone: 'green' | 'yellow' | 'red' | 'blue';
  icon: ComponentType<{
    className?: string;
  }>;
}
const KPIS: KpiCard[] = [{
  label: 'Réponses vérifiées',
  value: '98.7 %',
  tone: 'green',
  icon: ShieldCheckIcon
}, {
  label: 'Réponses à réviser',
  value: '14',
  tone: 'yellow',
  icon: AlertTriangleIcon
}, {
  label: 'Questions sans réponse officielle',
  value: '7',
  tone: 'red',
  icon: HelpCircleIcon
}, {
  label: 'Incohérences détectées',
  value: '3',
  tone: 'red',
  icon: GitCompareIcon
}, {
  label: 'Validation humaine requise',
  value: '5',
  tone: 'yellow',
  icon: UserCheckIcon
}, {
  label: 'Satisfaction citoyenne',
  value: '4.8 / 5',
  tone: 'blue',
  icon: StarIcon
}];
const TONE_STYLES: Record<KpiCard['tone'], {
  iconWrap: string;
  value: string;
  bar: string;
}> = {
  green: {
    iconWrap: 'bg-emerald-50 text-emerald-600',
    value: 'text-emerald-700',
    bar: 'bg-emerald-500'
  },
  yellow: {
    iconWrap: 'bg-amber-50 text-amber-600',
    value: 'text-amber-700',
    bar: 'bg-amber-400'
  },
  red: {
    iconWrap: 'bg-red-50 text-red-600',
    value: 'text-red-700',
    bar: 'bg-red-500'
  },
  blue: {
    iconWrap: 'bg-indigo-50 text-[#4F46E5]',
    value: 'text-[#4F46E5]',
    bar: 'bg-[#4F46E5]'
  }
};
const LOW_CONFIDENCE = [{
  q: 'Puis-je installer une clôture de 8 pieds ?',
  conf: 82,
  service: 'Urbanisme',
  freq: '18 demandes ce mois-ci',
  action: 'Réviser'
}, {
  q: 'Puis-je brûler des branches sur mon terrain ?',
  conf: 88,
  service: 'Environnement',
  freq: '12 demandes ce mois-ci',
  action: 'Assigner'
}, {
  q: 'Quel permis est requis pour une piscine hors-terre ?',
  conf: 91,
  service: 'Urbanisme',
  freq: '26 demandes ce mois-ci',
  action: 'Réviser'
}];
const UNANSWERED = [{
  q: 'Où puis-je recharger ma voiture électrique ?',
  freq: '32 questions ce mois-ci',
  service: 'Mobilité durable',
  action: 'Créer une réponse officielle'
}, {
  q: 'Puis-je louer une salle communautaire ?',
  freq: '18 questions ce mois-ci',
  service: 'Loisirs',
  action: 'Assigner un expert'
}, {
  q: 'Existe-t-il une subvention pour les arbres ?',
  freq: '14 questions ce mois-ci',
  service: 'Environnement',
  action: 'Compléter la base de connaissances'
}];
const VALIDATION_SERVICES = [{
  service: 'Urbanisme',
  pending: 4,
  time: '2 h'
}, {
  service: 'Travaux publics',
  pending: 3,
  time: '1 h 30'
}, {
  service: 'Environnement',
  pending: 2,
  time: '3 h'
}];
const OPPORTUNITIES = [{
  title: 'Créer une FAQ officielle sur les collectes de branches',
  reason: '127 citoyens ont posé une question similaire ce mois-ci.',
  impact: ['-34 appels par mois', '+18 % résolution en libre-service'],
  cta: 'Créer la réponse'
}, {
  title: 'Ajouter un bouton rapide « Signaler un nid-de-poule »',
  reason: '89 questions liées aux nids-de-poule ont été posées.',
  impact: ['+22 % de signalements structurés', '-15 % d’appels au 311'],
  cta: 'Créer l’action'
}, {
  title: 'Mettre à jour la page « permis piscine »',
  reason: 'La réponse actuelle génère un niveau de confiance moyen de 91 %.',
  impact: ['+7 points de confiance', 'Réduction des validations humaines'],
  cta: 'Mettre à jour la source'
}];
const AUDIT_LOG = [{
  date: '8 juin 2026',
  text: 'Réponse « collecte organique » bloquée sous le seuil de 95 %',
  tone: 'red' as const
}, {
  date: '8 juin 2026',
  text: 'Expert Urbanisme a approuvé une nouvelle réponse officielle',
  tone: 'green' as const
}, {
  date: '8 juin 2026',
  text: 'Incohérence détectée entre PDF et page Web',
  tone: 'yellow' as const
}, {
  date: '8 juin 2026',
  text: '42 réponses vérifiées générées sans intervention humaine',
  tone: 'blue' as const
}];
function confidenceBadge(conf: number) {
  if (conf < 85) {
    return {
      label: 'Expert humain requis',
      className: 'bg-red-50 text-red-700 border-red-200'
    };
  }
  return {
    label: 'Validation recommandée',
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  };
}
export function AtnaModule() {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('gerer');
  const [configSection, setConfigSection] = useState<ConfigSection>('connaissances');
  const [panelOpen, setPanelOpen] = useState(false);
  const [approved, setApproved] = useState(false);
  const [officialAnswer, setOfficialAnswer] = useState('');
  const openPanel = () => {
    setApproved(false);
    setOfficialAnswer('');
    setPanelOpen(true);
  };
  const tabs: {
    id: SubTab;
    label: string;
  }[] = [{
    id: 'gerer',
    label: 'Gérer'
  }, {
    id: 'analytiques',
    label: 'Analytiques'
  }, {
    id: 'configurer',
    label: 'Configurer'
  }];
  const configSections: {
    id: ConfigSection;
    label: string;
    desc: string;
  }[] = [{
    id: 'connaissances',
    label: 'Connaissances',
    desc: 'Sources, base de connaissances et réponses officielles'
  }, {
    id: 'automatisations',
    label: 'Automatisations',
    desc: 'Règles, déclencheurs et actions automatisées'
  }, {
    id: 'gouvernance',
    label: 'Gouvernance',
    desc: 'Seuils de confiance, validations et politiques'
  }];
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-407" data-mp="1bs6a94yozd4i">
      {/* Header */}
      <div className="mb-8" data-id="element-408" data-mp="1gr0xcj3oznuf">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4" data-id="element-409" data-mp="24iy2jrcmzhmk">
          <div data-id="element-410" data-mp="2994spvmsra6x">
            <div className="flex items-center gap-3" data-id="element-411" data-mp="299ob1dgd00lh">
              <div className="w-10 h-10 rounded-xl bg-[#0F172A] flex items-center justify-center" data-id="element-412" data-mp="29bhjmyaj8uw4">
                <SparklesIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-413" data-mp="2j3al9jcv9qhh" />
              </div>
              <div data-id="element-414" data-mp="2e73e5n8qi8xy">
                <h1 className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-415" data-mp="2e4hba0wsghm2">
                  ATNA AI
                </h1>
                <p className="text-sm text-slate-500" data-id="element-416" data-mp="2565bp9dy29hh">
                  Centre de supervision de l'intelligence municipale
                </p>
              </div>
            </div>
          </div>
          <nav aria-label="Sections" className="flex flex-wrap items-center gap-2" data-id="element-417" data-mp="1vfan3yd86xsv">
            {tabs.map(t => <button key={t.id} onClick={() => setActiveSubTab(t.id)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeSubTab === t.id ? 'text-white bg-[#0F172A] shadow-sm hover:bg-[#1E293B]' : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-418" data-mp="1qg7rt5n1s5y3">
                {t.label}
              </button>)}
          </nav>
        </div>
      </div>

      {activeSubTab === 'analytiques' ? <AtnaAnalytics data-id="element-419" data-mp="3o3s15laypjc0" /> : activeSubTab === 'configurer' ? <div className="flex flex-col lg:flex-row gap-6" data-id="element-420" data-mp="3t34sai8v9cke">
          {/* Section Switcher */}
          <div className="lg:w-60 flex-shrink-0" data-id="element-421" data-mp="1xd1rhr6fxx2">
            <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4" data-id="element-422" data-mp="1xldv7fh6z77">
              {configSections.map(s => <button key={s.id} onClick={() => setConfigSection(s.id)} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${configSection === s.id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-423" data-mp="5wccjizlaowf">
                  {s.label}
                </button>)}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-424" data-mp="3j71qxelu9hyi">
            <div className="mb-6" data-id="element-425" data-mp="2ml85f86ev5yv">
              <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-426" data-mp="2qonwlozpoe46">
                {configSections.find(s => s.id === configSection)?.label}
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-427" data-mp="1k3oci4gpzvt4">
                {configSections.find(s => s.id === configSection)?.desc}
              </p>
            </div>
            <div className="border border-dashed border-slate-200 rounded-xl p-12 text-center" data-id="element-428" data-mp="1u0j6ca21t812">
              <p className="text-slate-400" data-id="element-429" data-mp="1ozvjm0kurdcn">
                Section «{' '}
                {configSections.find(s => s.id === configSection)?.label} » (à
                venir)
              </p>
            </div>
          </div>
        </div> : <div className="space-y-8" data-id="element-430" data-mp="1xzil0mdl0j5p">
          {/* SECTION 1 — KPI Health */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-431" data-mp="2cppq80ekw5og">
            {KPIS.map(kpi => {
          const Icon = kpi.icon;
          const s = TONE_STYLES[kpi.tone];
          return <div key={kpi.label} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 relative overflow-hidden" data-id="element-432" data-mp="2lrgwp7gy0gjd">
                  <div className={`absolute top-0 left-0 w-full h-1 ${s.bar}`} data-id="element-433" data-mp="2hnj136qttnv2"></div>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${s.iconWrap}`} data-id="element-434" data-mp="30bjnv7ghca6h">
                    <Icon className="w-5 h-5" data-id="element-435" data-mp="35ad3b5fbub9a" />
                  </div>
                  <div className={`text-2xl font-bold ${s.value}`} data-id="element-436" data-mp="2rcg8adgiucwq">
                    {kpi.value}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug" data-id="element-437" data-mp="2wdvqlxrbe5bf">
                    {kpi.label}
                  </div>
                </div>;
        })}
          </div>

          {/* SECTION 2 — Attention requise */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-438" data-mp="28n03vf1pu0b1">
            <div className="p-6 border-b border-slate-100" data-id="element-439" data-mp="2cnjzge6n4vv2">
              <div className="flex items-center gap-2 mb-1" data-id="element-440" data-mp="2clz5apo8nexq">
                <AlertTriangleIcon className="w-5 h-5 text-amber-500" data-id="element-441" data-mp="1ypbw9oj0gybg" />
                <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-442" data-mp="23n3vz4pwdoq7">
                  ATNA nécessite votre attention
                </h2>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed max-w-3xl" data-id="element-443" data-mp="3udmkg6t8y64m">
                Les éléments ci-dessous sont détectés automatiquement lorsque la
                confiance est inférieure au seuil municipal de 95 %, lorsqu'une
                réponse officielle est manquante ou lorsque les sources se
                contredisent.
              </p>
            </div>

            {/* A — Low confidence */}
            <div className="p-6 border-b border-slate-100" data-id="element-444" data-mp="lvqfhcdev9ky">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4" data-id="element-445" data-mp="lux8qbegck2t">
                <h3 className="text-sm font-bold text-[#0F172A] uppercase tracking-wide" data-id="element-446" data-mp="76aukd0k9yd6">
                  Réponses sous le seuil de confiance
                </h3>
                <span className="text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full" data-id="element-447" data-mp="zuzlxkbtepbq">
                  Seuil municipal recommandé : 95 %
                </span>
              </div>
              <div className="overflow-x-auto" data-id="element-448" data-mp="2pbbz2sw9xz3o">
                <table className="w-full text-left border-collapse" data-id="element-449" data-mp="2kdjxf75tuyma">
                  <thead data-id="element-450" data-mp="2kda5he182hdy">
                    <tr className="border-b border-slate-200" data-id="element-451" data-mp="2y4xe8dccpidm">
                      <th className="px-3 py-2 text-xs font-semibold text-slate-500" data-id="element-452" data-mp="2y4fbqyz3i7x9">
                        Question citoyenne
                      </th>
                      <th className="px-3 py-2 text-xs font-semibold text-slate-500" data-id="element-453" data-mp="26d93fv9813ww">
                        Confiance
                      </th>
                      <th className="px-3 py-2 text-xs font-semibold text-slate-500" data-id="element-454" data-mp="21gipbdgz8ie4">
                        Service concerné
                      </th>
                      <th className="px-3 py-2 text-xs font-semibold text-slate-500" data-id="element-455" data-mp="2aeuoi3vwsoi6">
                        Fréquence
                      </th>
                      <th className="px-3 py-2 text-xs font-semibold text-slate-500 text-right" data-id="element-456" data-mp="3qrrlu9o2kn9o">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-457" data-mp="2jgtr2bgavk00">
                    {LOW_CONFIDENCE.map(row => {
                  const badge = confidenceBadge(row.conf);
                  return <tr key={row.q} className="hover:bg-slate-50" data-id="element-458" data-mp="2sj31707npbcv">
                          <td className="px-3 py-3 text-sm text-slate-800 font-medium max-w-xs" data-id="element-459" data-mp="2xh4mzswgvos1">
                            {row.q}
                          </td>
                          <td className="px-3 py-3" data-id="element-460" data-mp="20osoay0en9lb">
                            <div className="flex items-center gap-2" data-id="element-461" data-mp="24t0fd1uc51mp">
                              <span className="text-sm font-bold text-slate-700" data-id="element-462" data-mp="29qs6vur2ilqd">
                                {row.conf} %
                              </span>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.className}`} data-id="element-463" data-mp="2ek3cjsk58wgp">
                                {badge.label}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-600" data-id="element-464" data-mp="1ehoknjy8syh2">
                            {row.service}
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-500" data-id="element-465" data-mp="14jqwsxkap3oz">
                            {row.freq}
                          </td>
                          <td className="px-3 py-3 text-right" data-id="element-466" data-mp="10hvxfylxd0du">
                            <button onClick={openPanel} className="px-3 py-1.5 text-xs font-semibold text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors" data-id="element-467" data-mp="vjuhhmjsph6l">
                              {row.action}
                            </button>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* B — Unanswered */}
            <div className="p-6 border-b border-slate-100" data-id="element-468" data-mp="7trtbhj04vd9">
              <h3 className="text-sm font-bold text-[#0F172A] uppercase tracking-wide mb-4" data-id="element-469" data-mp="7ujm6kl7sezu">
                Questions sans réponse officielle
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-470" data-mp="2bxuo03zqlraq">
                {UNANSWERED.map(item => <div key={item.q} className="border border-slate-200 rounded-xl p-4 flex flex-col" data-id="element-471" data-mp="1y6zk7643i0e0">
                    <p className="text-sm font-semibold text-slate-800 mb-2" data-id="element-472" data-mp="234houkb2xtg7">
                      {item.q}
                    </p>
                    <p className="text-xs text-slate-500 mb-1" data-id="element-473" data-mp="1o6p67ja8koxs">{item.freq}</p>
                    <p className="text-xs text-slate-500 mb-4" data-id="element-474" data-mp="1t7wgbozuq07m">
                      Service suggéré :{' '}
                      <span className="font-medium text-slate-700" data-id="element-475" data-mp="1f8wqie0lrf9v">
                        {item.service}
                      </span>
                    </p>
                    <button className="mt-auto px-3 py-2 text-xs font-semibold text-white bg-[#0F172A] rounded-lg hover:bg-[#1E293B] transition-colors" data-id="element-476" data-mp="39kto6opn1hdd">
                      {item.action}
                    </button>
                  </div>)}
              </div>
              <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-100 rounded-xl" data-id="element-477" data-mp="3oma9prw21z7r">
                <SparklesIcon className="w-5 h-5 text-[#4F46E5] flex-shrink-0 mt-0.5" data-id="element-478" data-mp="3jngunv14e05h" />
                <p className="text-sm text-indigo-900" data-id="element-479" data-mp="1dewxon7ki5k">
                  ATNA a identifié{' '}
                  <span className="font-bold" data-id="element-480" data-mp="3snvgqwlwrja1">64 questions citoyennes</span> qui
                  pourraient être résolues automatiquement si ces réponses
                  étaient validées.
                </p>
              </div>
            </div>

            {/* C — Inconsistency */}
            <div className="p-6" data-id="element-481" data-mp="k9mwcbtjhysy">
              <h3 className="text-sm font-bold text-[#0F172A] uppercase tracking-wide mb-4" data-id="element-482" data-mp="g8l4wh5v6r1j">
                Incohérences détectées entre sources
              </h3>
              <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-5" data-id="element-483" data-mp="2m21ijn349pf6">
                <div className="flex items-start gap-2 mb-4" data-id="element-484" data-mp="2i199atu8vkiw">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-485" data-mp="kztpkwz8jl1x" />
                  <div data-id="element-486" data-mp="py4x31225jgz">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide" data-id="element-487" data-mp="pzg2t6f2d1aq">
                      Question
                    </p>
                    <p className="text-sm font-semibold text-slate-800" data-id="element-488" data-mp="uzubmqohof9t">
                      Quand est la collecte des matières organiques ?
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-489" data-mp="18x9300yayx4f">
                  <div className="bg-white border border-slate-200 rounded-lg p-3" data-id="element-490" data-mp="18v64stmzxxom">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1" data-id="element-491" data-mp="1dor6avnst9sk">
                      <GlobeIcon className="w-3.5 h-3.5" data-id="element-492" data-mp="1imizdt06j1yc" /> Source A — Page Web
                    </div>
                    <div className="text-xs text-slate-400 mb-2" data-id="element-493" data-mp="1nnzxxhv6ycbr">
                      windsor.ca/collectes
                    </div>
                    <div className="text-sm" data-id="element-494" data-mp="3c5t1m54libfr">
                      Réponse détectée :{' '}
                      <span className="font-bold text-slate-800" data-id="element-495" data-mp="3847g5vdzwoty">mardi</span>
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-3" data-id="element-496" data-mp="334l3po5zrcm7">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1" data-id="element-497" data-mp="2y69sb901su1v">
                      <FileTextIcon className="w-3.5 h-3.5" data-id="element-498" data-mp="3vtsb7cnfbcjl" /> Source B — Guide
                      PDF 2026
                    </div>
                    <div className="text-xs text-slate-400 mb-2" data-id="element-499" data-mp="3qvqhzywa9wue">
                      Document officiel
                    </div>
                    <div className="text-sm" data-id="element-500" data-mp="3m24g91rnrddd">
                      Réponse détectée :{' '}
                      <span className="font-bold text-slate-800" data-id="element-501" data-mp="3h4cme27gdh6j">mercredi</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 mb-4 text-sm" data-id="element-502" data-mp="dofb1pxk236m">
                  <span className="text-slate-600" data-id="element-503" data-mp="3i5ypfsfc3g1q">
                    Confiance :{' '}
                    <span className="font-bold text-amber-700" data-id="element-504" data-mp="3i45gg6h90jqk">76 %</span>
                  </span>
                  <span className="text-slate-600" data-id="element-505" data-mp="3myjgim6uvf1k">
                    Impact estimé :{' '}
                    <span className="font-medium text-slate-800" data-id="element-506" data-mp="3937cfo6t8c7z">
                      241 citoyens pourraient recevoir une réponse ambiguë.
                    </span>
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-507" data-mp="9q6mfadu78fp">
                  <button className="px-3 py-2 text-xs font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-508" data-mp="9ovlrjfougst">
                    Choisir la réponse officielle
                  </button>
                  <button className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-509" data-mp="nno93kzxjzs">
                    Assigner au service responsable
                  </button>
                  <button className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-510" data-mp="2cy5v2gkezmpd">
                    Mettre à jour la source
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 3 — Human validation */}
          <div data-id="element-511" data-mp="2hvp8xfd79xgs">
            <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-512" data-mp="1u87d98qtef8j">
              <UserCheckIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-513" data-mp="1p94lgpzui5ii" /> Besoin d'un
              expert humain
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-514" data-mp="23yiq5ksh7yrk">
              {VALIDATION_SERVICES.map(s => <div key={s.service} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5" data-id="element-515" data-mp="1z6si5lh0hn3b">
                  <h3 className="text-base font-bold text-[#0F172A] mb-2" data-id="element-516" data-mp="1bgy6h78fi4kn">
                    {s.service}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-1" data-id="element-517" data-mp="16id1kui8ewpv">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200" data-id="element-518" data-mp="2yypvp9ufmed">
                      {s.pending} validations en attente
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-4" data-id="element-519" data-mp="7x07e5rmfdnq">
                    <ClockIcon className="w-3.5 h-3.5" data-id="element-520" data-mp="loom8gt98g8e" /> Temps moyen de
                    traitement : {s.time}
                  </div>
                  <button onClick={openPanel} className="w-full px-3 py-2 text-sm font-semibold text-white bg-[#0F172A] rounded-lg hover:bg-[#1E293B] transition-colors flex items-center justify-center gap-2" data-id="element-521" data-mp="gp26o0pyenwf">
                    Voir les demandes <ArrowRightIcon className="w-4 h-4" data-id="element-522" data-mp="qlyfy8nofe21" />
                  </button>
                </div>)}
            </div>
          </div>

          {/* SECTION 4 — Opportunities */}
          <div data-id="element-523" data-mp="2sgz679kxr21q">
            <h2 className="text-lg font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-524" data-mp="2shhamthr4mkq">
              <SparklesIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-525" data-mp="17zpxbj8mtugp" /> Opportunités
              détectées par ATNA
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-526" data-mp="y82bbxuuh5mm">
              {OPPORTUNITIES.map(o => <div key={o.title} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 flex flex-col" data-id="element-527" data-mp="136dmqd0sfo6y">
                  <h3 className="text-sm font-bold text-[#0F172A] mb-3" data-id="element-528" data-mp="p6m9wfm0cduj">
                    {o.title}
                  </h3>
                  <p className="text-xs text-slate-500 mb-3" data-id="element-529" data-mp="ta0tfg95nccq">
                    <span className="font-semibold text-slate-600" data-id="element-530" data-mp="f97tuh3zxr2r">
                      Raison :{' '}
                    </span>
                    {o.reason}
                  </p>
                  <div className="space-y-1 mb-4" data-id="element-531" data-mp="29mz1jb2rm0vk">
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide" data-id="element-532" data-mp="29n8rwzrq2a1s">
                      Impact estimé
                    </p>
                    {o.impact.map(i => <div key={i} className="flex items-center gap-1.5 text-xs text-emerald-700" data-id="element-533" data-mp="2d5oey42fp202">
                        <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-500" data-id="element-534" data-mp="284ika0p7r4nw" />{' '}
                        {i}
                      </div>)}
                  </div>
                  <button className="mt-auto px-3 py-2 text-sm font-semibold text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors" data-id="element-535" data-mp="1y7m9fobue6g6">
                    {o.cta}
                  </button>
                </div>)}
            </div>
          </div>

          {/* SECTIONS 5 & 6 — Audit log + Confidence rules */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-536" data-mp="2m3qk8vt7agt5">
            {/* Audit log */}
            <div className="lg:col-span-8 bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-id="element-537" data-mp="2h5p34gfc4rkb">
              <div className="flex items-center justify-between mb-4" data-id="element-538" data-mp="3emxassgnurrd">
                <h2 className="text-base font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-539" data-mp="39tup16i4kmlq">
                  <ScrollTextIcon className="w-5 h-5 text-slate-500" data-id="element-540" data-mp="3i4v354kczdju" /> Journal
                  d'audit ATNA
                </h2>
                <button className="text-sm font-semibold text-[#4F46E5] hover:text-[#4338CA] transition-colors" data-id="element-541" data-mp="3m77wsultzl09">
                  Voir tout le journal
                </button>
              </div>
              <ul className="space-y-3" data-id="element-542" data-mp="3w4m9qk8jprnt">
                {AUDIT_LOG.map((e, i) => {
              const dot = e.tone === 'green' ? 'bg-emerald-500' : e.tone === 'yellow' ? 'bg-amber-400' : e.tone === 'red' ? 'bg-red-500' : 'bg-[#4F46E5]';
              return <li key={i} className="flex items-start gap-3 text-sm" data-id="element-543" data-mp="doto6ih077yn" data-mpi={i}>
                      <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dot}`} data-id="element-544" data-mp="ioewvklz6uok"></span>
                      <span className="text-slate-400 font-medium whitespace-nowrap" data-id="element-545" data-mp="nnhsyfjz9qke">
                        {e.date}
                      </span>
                      <span className="text-slate-700" data-id="element-546" data-mp="sndxcfwl7k0h">{e.text}</span>
                    </li>;
            })}
              </ul>
            </div>

            {/* Confidence rules */}
            <div className="lg:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-id="element-547" data-mp="1kfoawzj1mcrx">
              <h2 className="text-base font-bold text-[#0F172A] font-heading mb-4 flex items-center gap-2" data-id="element-548" data-mp="1z42b12p2rn4s">
                <SlidersHorizontalIcon className="w-5 h-5 text-slate-500" data-id="element-549" data-mp="1u5r16rys5cmk" />{' '}
                Règles de confiance
              </h2>
              <dl className="space-y-3 text-sm" data-id="element-550" data-mp="16h7swcfqczuh">
                <div className="flex justify-between gap-3" data-id="element-551" data-mp="11gtuzn7uf6a6">
                  <dt className="text-slate-500" data-id="element-552" data-mp="11idcfh58jndj">Seuil de réponse autonome</dt>
                  <dd className="font-bold text-[#4F46E5]" data-id="element-553" data-mp="1fh4neuoswsxg">95 %</dd>
                </div>
                <div className="flex justify-between gap-3" data-id="element-554" data-mp="1bcn5z25wyrpu">
                  <dt className="text-slate-500" data-id="element-555" data-mp="2qufdd7nte5o2">Entre 85 % et 95 %</dt>
                  <dd className="font-medium text-amber-700 text-right" data-id="element-556" data-mp="2mtdg2wdgk3oq">
                    Validation recommandée
                  </dd>
                </div>
                <div className="flex justify-between gap-3" data-id="element-557" data-mp="2ubbadtn2j87d">
                  <dt className="text-slate-500" data-id="element-558" data-mp="2zcijpx4v0fg5">Sous 85 %</dt>
                  <dd className="font-medium text-red-700 text-right" data-id="element-559" data-mp="2z9mj211mnco0">
                    Bloquée + expert requis
                  </dd>
                </div>
                <div className="flex justify-between gap-3" data-id="element-560" data-mp="2pekub6b9kcly">
                  <dt className="text-slate-500" data-id="element-561" data-mp="3d3nm36zm3lu6">Sources contradictoires</dt>
                  <dd className="font-medium text-slate-700 text-right" data-id="element-562" data-mp="3d5q8zi5gwyuo">
                    Ne pas répondre
                  </dd>
                </div>
                <div className="flex justify-between gap-3" data-id="element-563" data-mp="33cj92cthyb1z">
                  <dt className="text-slate-500" data-id="element-564" data-mp="38cnmftz3ypnz">Aucune source officielle</dt>
                  <dd className="font-medium text-slate-700 text-right" data-id="element-565" data-mp="38ako8mnsxq86">
                    Escalader vers la Ville
                  </dd>
                </div>
              </dl>
              <button className="mt-5 w-full px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-566" data-mp="4p570lb97612">
                Modifier les règles
              </button>
            </div>
          </div>

          {/* Strategic banner */}
          <div className="bg-[#0F172A] rounded-xl p-6 text-center" data-id="element-567" data-mp="5hbd317jhq1a">
            <div className="flex items-center justify-center gap-2 mb-1" data-id="element-568" data-mp="kky6h7h10hg">
              <ShieldCheckIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-569" data-mp="3rpt23nokf2kf" />
              <p className="text-white font-bold" data-id="element-570" data-mp="3rs5terg7qpa5">ATNA ne devine pas.</p>
            </div>
            <p className="text-slate-300 text-sm max-w-2xl mx-auto" data-id="element-571" data-mp="3my1kw3rau96y">
              ATNA vérifie, signale les incertitudes et demande l'aide d'un
              expert humain lorsque nécessaire.
            </p>
          </div>
        </div>}

      {/* Side Panel — Validation */}
      {panelOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-572" data-mp="38z1w8w3sduas">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setPanelOpen(false)} data-id="element-573" data-mp="33zyrzdvmrs3v"></div>
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200" data-id="element-574" data-mp="1jheghr1mbitb">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between" data-id="element-575" data-mp="1ojn9uoiekmvy">
              <h3 className="text-base font-bold text-[#0F172A]" data-id="element-576" data-mp="1xi8nq6cxjdqe">
                Validation d'une réponse officielle
              </h3>
              <button onClick={() => setPanelOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-id="element-577" data-mp="10t13r2rylcol">
                <XIcon className="w-5 h-5" data-id="element-578" data-mp="15oyg6y03pyw3" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-579" data-mp="19rd3pveptmtp">
              <div data-id="element-580" data-mp="1ejwfcmtue7vv">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1" data-id="element-581" data-mp="1ekejs6qnrsev">
                  Question citoyenne
                </p>
                <p className="text-sm font-semibold text-slate-800" data-id="element-582" data-mp="hznp688jziv5">
                  Puis-je installer une clôture de 8 pieds ?
                </p>
              </div>

              <div data-id="element-583" data-mp="3blnkjfnvo3ou">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1" data-id="element-584" data-mp="3bn86wi01ggbm">
                  Réponse proposée par ATNA
                </p>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-id="element-585" data-mp="3lkcride8k3qb">
                  ATNA n'a pas trouvé de réponse officielle suffisamment fiable.
                </div>
              </div>

              <div data-id="element-586" data-mp="3ui6l3gkm8j9s">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2" data-id="element-587" data-mp="3pkwq7legjfek">
                  Sources consultées
                </p>
                <ul className="space-y-1.5" data-id="element-588" data-mp="833rwc1aevti">
                  {[{
                icon: BookOpenIcon,
                label: "Règlement d'urbanisme"
              }, {
                icon: GlobeIcon,
                label: 'Page Web permis'
              }, {
                icon: FileTextIcon,
                label: 'Guide citoyen PDF'
              }].map(s => {
                const I = s.icon;
                return <li key={s.label} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-589" data-mp="vsbqqo0493he">
                        <I className="w-4 h-4 text-slate-400" data-id="element-590" data-mp="10qd9t8xjl2sv" /> {s.label}
                      </li>;
              })}
                </ul>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-591" data-mp="rpp5x71v4itp">
                <span className="text-sm text-slate-500" data-id="element-592" data-mp="d12iei230kre">Confiance</span>
                <span className="text-sm font-bold text-amber-700" data-id="element-593" data-mp="hrr7jqqah5nd">82 %</span>
              </div>

              <div data-id="element-594" data-mp="40w5axaapmsr">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-2" data-id="element-595" data-mp="41fe9ohyx0un">
                  Réponse officielle à approuver
                </label>
                <textarea value={officialAnswer} onChange={e => setOfficialAnswer(e.target.value)} rows={5} placeholder="Rédigez la réponse officielle de la Ville…" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] outline-none resize-none" data-id="element-596" data-mp="3q8475pm0bbk1" />
              </div>

              {approved && <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-597" data-mp="1d8lld1jwvqg">
                  <div className="flex items-center gap-2 mb-1" data-id="element-598" data-mp="5diq0l9p3e37">
                    <CheckCircle2Icon className="w-5 h-5 text-emerald-600" data-id="element-599" data-mp="adwrtlkpdrss" />
                    <p className="font-bold text-emerald-800" data-id="element-600" data-mp="fcht7oboyhit">
                      Connaissance ajoutée.
                    </p>
                  </div>
                  <p className="text-sm text-emerald-700" data-id="element-601" data-mp="ka9s52ar73wi">
                    Les prochaines réponses liées à ce sujet seront générées
                    avec un niveau de confiance estimé de 98 %.
                  </p>
                </div>}

              <div className="space-y-2 pt-2" data-id="element-602" data-mp="y2zitk188wzy">
                <button onClick={() => setApproved(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-603" data-mp="y4al1ff0xwoy">
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-604" data-mp="1d7aoedmqwxn8" /> Approuver comme
                  réponse officielle
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-605" data-mp="137jr646sw4jg">
                  <UserCheckIcon className="w-4 h-4" data-id="element-606" data-mp="1vx0g2rv7p3ax" /> Assigner à un autre
                  expert
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-607" data-mp="1qyp6mi8twut8">
                  <HelpCircleIcon className="w-4 h-4" data-id="element-608" data-mp="1i4kkgc4n47k1" /> Publier dans la FAQ
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-609" data-mp="2eojhwvdi2bbd">
                  <LanguagesIcon className="w-4 h-4" data-id="element-610" data-mp="80sy72p6oe2i" /> Traduire en anglais
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-611" data-mp="czvsbs3mkzvp">
                  <RepeatIcon className="w-4 h-4" data-id="element-612" data-mp="35n3bedszp2a" /> Utiliser pour les
                  prochaines réponses
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}